import {APP_VERSION} from '../../constants/version';
import {API_BASE} from '../apiConfig';
import {isEnvConfEnabled} from "../env/isEnvConfEnabled";
import {ENV_CONFIG_VARIABLES} from "../../constants/env";

// Версия, ради которой эта ВКЛАДКА уже перезагружалась, — защита от петли
// перезагрузок, если после обновления страница все равно приезжает старой.
// Хранится в sessionStorage, а не в localStorage: ключ там общий на все
// вкладки, и после автообновления одной остальные считали свою перезагрузку
// уже израсходованной — вместо тихого обновления показывали оверлей
// «Приложение устарело»
const LAST_AUTO_RELOAD_VERSION_KEY = 'lastAutoReloadVersion';

// sessionStorage может быть недоступен (приватный режим, запрет сторонних
// данных) — без него просто теряется защита от петли, но не работа проверки
const readLastAutoReloadVersion = () => {
  try {
    return window.sessionStorage.getItem(LAST_AUTO_RELOAD_VERSION_KEY);
  } catch (e) {
    return null;
  }
};

const writeLastAutoReloadVersion = (version) => {
  try {
    window.sessionStorage.setItem(LAST_AUTO_RELOAD_VERSION_KEY, version);
  } catch (e) {
    // Хранилище недоступно — продолжаем без защиты от повторной перезагрузки
  }
};

const clearLastAutoReloadVersion = () => {
  try {
    window.sessionStorage.removeItem(LAST_AUTO_RELOAD_VERSION_KEY);
    // Чистим и старое место хранения: у давно игравших ключ мог остаться в
    // localStorage и продолжал бы подавлять автообновление в новых вкладках
    window.localStorage.removeItem(LAST_AUTO_RELOAD_VERSION_KEY);
  } catch (e) {
    // Хранилище недоступно — чистить нечего
  }
};

/**
 * Сравнивает версии по числовым частям: -1 если left старее right, 1 если
 * новее, 0 при равенстве. null — если версию не разобрать.
 *
 * Сравнивать строками нельзя: '0.1.9' и '0.1.10' так идут в обратном порядке
 */
const compareAppVersions = (left, right) => {
  const parseVersion = (version) => String(version).split('.').map((part) => Number(part));
  const leftParts = parseVersion(left);
  const rightParts = parseVersion(right);
  if (leftParts.some((part) => Number.isNaN(part)) || rightParts.some((part) => Number.isNaN(part))) {
    return null;
  }

  const partsCount = Math.max(leftParts.length, rightParts.length);
  for (let i = 0; i < partsCount; i++) {
    const leftPart = leftParts[i] || 0;
    const rightPart = rightParts[i] || 0;
    if (leftPart !== rightPart) return leftPart < rightPart ? -1 : 1;
  }
  return 0;
};

// Пока true, проверка версии не делает НИЧЕГО: ни перезагрузки, ни оверлея.
// Нужно на экране ввода никнейма (см. UserNamePage) — перезагрузка посреди
// регистрации теряет введенное имя, и пользователь получает имя из аккаунта
// Яндекса, которое он как раз менял
let isAutoReloadSuspended = false;

// Между решением перезагрузиться и фактическим уходом страницы проходит
// заметное время: сначала снимаются service worker-ы, потом браузер тянет
// новый документ по сети, а старая страница все это время живет и продолжает
// проверять версию (интервал, возврат на вкладку, bfcache). Такая параллельная
// проверка видела уже записанную отметку попытки и показывала оверлей
// «Приложение устарело» — то самое повторное предложение обновиться прямо
// поверх идущего автообновления. С момента решения проверка больше ничего не
// делает: перезагрузка уже в пути
let isReloadingForNewVersion = false;

export function setVersionAutoReloadSuspended(isSuspended) {
  isAutoReloadSuspended = Boolean(isSuspended);
}

export async function checkAppVersion() {
  if (isAutoReloadSuspended || isReloadingForNewVersion) return;

  try {
    const res = await fetch(`${API_BASE}/version?_=${Date.now()}`, {
      method: 'GET',
      headers: {Accept: 'application/json'},
      cache: 'no-store',
    });
    if (!res.ok) return;

    const {version: serverVersion} = await res.json();
    if (!serverVersion) return;

    // Сравниваем по числовым частям, а не по строкам: '0.1.9' и '0.01.9' —
    // одна и та же версия, а строгое строковое неравенство отправляло бы
    // клиента в бесконечное «обновись» на ровном месте
    const versionOrder = compareAppVersions(APP_VERSION, serverVersion);
    const isSameVersion = versionOrder === null
      ? serverVersion === APP_VERSION
      : versionOrder === 0;

    if (!isSameVersion) {
      // Перезагружаем ТОЛЬКО когда клиент отстал от сервера: если он впереди,
      // обновлять нечего — свежее того, что уже загружено, страница не
      // получит. Так бывает во время раскатки, когда запрос попадает на еще
      // не обновленный инстанс: раньше клиент в этот момент видел «новое»
      // расхождение и перезагружался второй раз подряд.
      // Версию, которую не удалось разобрать, сравнить нельзя — там остается
      // прежнее поведение «любое расхождение = одна перезагрузка»
      if (versionOrder !== null && versionOrder > 0) {
        console.log(
          `[VersionCheck] Mismatch: client=${APP_VERSION}, server=${serverVersion}.`
          + ' Client is ahead of the server, nothing to reload for.'
        );
        return;
      }

      const lastReloadedVersion = readLastAutoReloadVersion();

      if (lastReloadedVersion === serverVersion) {
        console.error(
          `[VersionCheck] Mismatch: client=${APP_VERSION}, server=${serverVersion}. Already auto-reloaded once, skipping to avoid loop.`
        );
        if (!isEnvConfEnabled(ENV_CONFIG_VARIABLES.REACT_APP_DISABLE_VERSION_MISMATCH)) {
          window.dispatchEvent(
            new CustomEvent('versionmismatch', {
              detail: {serverVersion, clientVersion: APP_VERSION},
            })
          );
        }
        return;
      }

      console.log(
        `[VersionCheck] Mismatch: client=${APP_VERSION}, server=${serverVersion}. Triggering auto-reload...`
      );
      // С этого момента проверка версии выключена до конца жизни страницы:
      // параллельные проверки не должны показать оверлей поверх идущего
      // автообновления
      isReloadingForNewVersion = true;
      writeLastAutoReloadVersion(serverVersion);
      // Unregister service workers to bypass SW cache on reload
      if (navigator.serviceWorker) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((reg) => reg.unregister()));
        } catch (swErr) {
          console.error('[VersionCheck] Failed to unregister SW:', swErr);
        }
      }
      // Обновление версии — не уход со страницы: глобальный обработчик
      // beforeunload (см. App) должен промолчать. Без этого флага браузер на
      // programmatic reload показывал НАТИВНОЕ окно «Перезагрузить сайт?» —
      // оно и всплывало у игроков в случайные моменты, а отмена в нем
      // оставляла клиент на старой версии с уже израсходованной попыткой
      window.__skipBeforeUnload = true;
      window.location.reload();
      return;
    }

    // Versions match — clear the flag so future updates will trigger reload
    clearLastAutoReloadVersion();
  } catch (e) {
    console.error('[VersionCheck] Failed to check version:', e);
  }
}

export function startPeriodicVersionCheck(intervalMs = 10 * 60 * 1000) {
  // Immediate first check on startup is expected to be called separately

  // Check when user returns to the tab after switching away
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkAppVersion();
    }
  });

  // Check when page is restored from back-forward cache (bfcache)
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      checkAppVersion();
    }
  });

  // Periodic background check
  const intervalId = setInterval(() => {
    checkAppVersion();
  }, intervalMs);

  return () => clearInterval(intervalId);
}
